/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { DefaultRouteTabs, SaveButton } from '@/components/_common';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { useRouteMatchParam } from '@/hooks';

import { useBankAccount } from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';
import { Divider, Form, Skeleton, UploadFile } from 'antd';
import { useIntl } from 'react-intl';
import { PageProps } from '@/types/PageProps';
import { useBankAccountFormContext } from '../../BankAccountFormProvider';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import ConfigBankSlipHeader from '@/components/Registry/BankAccounts/Config/ConfigBankSlipHeader';
import { useBankAccountSettings } from '@/core/domain/enterprise/BankAccountsSettings/BankAccountsSettings.hooks';
import moment from 'moment';
import {
  BankAccountSettings,
  BankAccountSettingsFormValues,
  BankAccountSettingsMessages,
} from '@/core/domain/enterprise/BankAccountsSettings/BankAccountsSettings.types';

import { v4 } from 'uuid';
import { RcFile } from 'antd/lib/upload';
import useBankAccountConfigSubmit from './BankAccountConfigSubmit.hooks';
import ConfigTabsForm from '@/components/Registry/BankAccounts/Config/ConfigTabsForm';

const BankAccountConfigBankSlipPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const bankAccountId = +useRouteMatchParam('bankAccountId');

  const [form] = Form.useForm<BankAccountSettingsFormValues>();

  const [tabErrors, setTabErrors] = useState<string[]>([]);
  const { setParamLabel } = useBreadcrumbs();

  const { contextForm, disabled } = useBankAccountFormContext();

  const bankAccount = useBankAccount(bankAccountId);

  useEffect(() => {
    if (bankAccount.data) {
      setParamLabel(':bankAccountId', bankAccount.data.bank.name);
    }
  }, [bankAccount.data]);

  const bankAccountSettings = useBankAccountSettings(bankAccountId);

  function handleBlobArchive(bytes?: string) {
    return new Blob(bytes ? [bytes] : [], { type: 'application/x-pkcs12' });
  }

  function handleAgreementWsConsult() {
    const bankCode = bankAccount.data?.bank.code;

    if (bankCode) {
      if (['237', '341', '756', '104'].includes(bankCode)) {
        return 1;
      }
    }

    if (bankAccountSettings.data?.bankAccountAgreement) {
      const bankAccountAgreement =
        bankAccountSettings.data.bankAccountAgreement;

      return typeof bankAccountAgreement?.agreementWsConsult !== 'undefined'
        ? Number(bankAccountAgreement?.agreementWsConsult)
        : undefined;
    }

    return undefined;
  }

  function handleAgreementBankOurNumber() {
    const bankCode = bankAccount.data?.bank.code;

    if (bankCode === '077') {
      return 1;
    }

    if (bankAccountSettings.data?.bankAccountAgreement) {
      const bankAccountAgreement =
        bankAccountSettings.data.bankAccountAgreement;

      return typeof bankAccountAgreement?.agreementBankOurNumber !== 'undefined'
        ? Number(bankAccountAgreement?.agreementBankOurNumber)
        : undefined;
    }

    return undefined;
  }

  function handleAgreementInstantRegister() {
    const bankCode = bankAccount.data?.bank.code;

    if (bankCode === '077') {
      return 1;
    }

    if (bankAccountSettings.data?.bankAccountAgreement) {
      const bankAccountAgreement =
        bankAccountSettings.data.bankAccountAgreement;

      return typeof bankAccountAgreement?.agreementInstantRegister !==
        'undefined'
        ? Number(bankAccountAgreement?.agreementInstantRegister)
        : undefined;
    }

    return undefined;
  }

  useEffect(() => {
    if (contextForm.bankAccountConfigBullet) {
      form.setFieldsValue(contextForm.bankAccountConfigBullet);
    } else if (bankAccountSettings.data) {
      const bankAccountAgreement =
        bankAccountSettings.data.bankAccountAgreement;
      const data = bankAccountSettings.data;

      const verifyAgreementRestartDaily =
        typeof bankAccountAgreement?.agreementRestartDaily !== 'undefined'
          ? Number(bankAccountAgreement?.agreementRestartDaily)
          : bankAccount.data?.bank.code === '237'
          ? 0
          : undefined;

      const messages = Object.keys(data || {}).filter((key) =>
        key.includes('titleMessage'),
      ) as (keyof BankAccountSettings)[];

      let certificateFile: UploadFile | undefined;

      if (!!data?.certificate?.certificateFile) {
        const file = new File(
          [handleBlobArchive(data.certificate?.certificateFile)],
          'name',
          { lastModified: new Date() as unknown as number },
        );

        certificateFile = {
          uid: v4(),
          name: 'Certificado Digital Cadastrado',
          status: 'done',
          originFileObj: file as RcFile,
        };
      }

      form.setFieldsValue({
        general: {
          ...bankAccountAgreement,
          agreementBankOurNumber: handleAgreementBankOurNumber(),
          agreementInstantRegister: handleAgreementInstantRegister(),
          agreementWsConsult: handleAgreementWsConsult(),

          agreementRestartDaily: verifyAgreementRestartDaily,
          agreementHybridEmission:
            typeof bankAccountAgreement?.agreementHybridEmission !== 'undefined'
              ? Number(bankAccountAgreement?.agreementHybridEmission)
              : undefined,

          titleAcceptance: data?.titleAcceptance,
          titleDocumentSpecies: data?.titleDocumentSpecies,
          titleVariationWallet: data?.titleVariationWallet,
          paymentDateType: data.paymentDateType,
          agreementAilosPassword:
            data.bankAccountAgreement?.agreementAilosPassword,
          agreementSandboxEnvironment:
            typeof bankAccountAgreement?.agreementSandboxEnvironment !==
            'undefined'
              ? Number(bankAccountAgreement?.agreementSandboxEnvironment)
              : 1,

          hasBankFee: data.hasBankFee,
          bankFee: data.bankFee,
          planAccountId: data.planAccount?.id,
          titleCodeReference: data.titleCodeReference,
          titleIdentificationTypePayment: data.titleIdentificationTypePayment,
        },

        certificate: !!data.certificate
          ? {
              certificateFile: [certificateFile] as UploadFile[],
              certificateAlias: data?.certificate?.certificateAlias,
              certificateEmail: data?.certificate?.certificateEmail,
              certificatePassword: data?.certificate?.certificatePassword,
              certificateExpirationDate: data?.certificate?.expirationDate
                ? moment(data.certificate?.expirationDate)
                : undefined,
            }
          : undefined,

        interest: {
          titleInterestCode: data?.titleInterestCode,
          titleInterestDays: data?.titleInterestDays,
          titleInterestValue: data?.titleInterestValue,
        },

        discount: {
          ...data,
        },

        fine: {
          ...data,
        },

        protest: {
          ...data,
        },

        messages: messages.map((key) => ({
          titleMessage: (data as BankAccountSettings)[key],
        })) as BankAccountSettingsMessages[],
      });
    }
  }, [bankAccountSettings.dataUpdatedAt]);

  const handleBankAccountConfigSubmit = useBankAccountConfigSubmit({
    bankAccount: bankAccount?.data,
    form,
    setTabErrors,
    bankAccountId,
  });

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={route.parent}
      baseKey={String(bankAccountId)}
      basePath={`/app/financial/accounts/bankAccounts/${bankAccountId}`}
      extra={
        <SaveButton
          block
          onClick={handleBankAccountConfigSubmit.handleSubmit}
          loading={handleBankAccountConfigSubmit.isLoading}
        >
          {intl.formatMessage({ id: 'commons.entity.save' })}
        </SaveButton>
      }
    >
      <Skeleton loading={bankAccountSettings.isLoading}>
        <ConfigBankSlipHeader bankAccount={bankAccount?.data} />

        <Form form={form}>
          <ConfigTabsForm
            tabErrors={tabErrors}
            bankAccount={bankAccount.data}
            bankAccountSettings={bankAccountSettings.data}
          />
        </Form>

        <Divider />

        <SpaceForButton>
          <ButtonBack
            path="/app/financial/accounts/bankAccounts"
            disabled={disabled}
          />
        </SpaceForButton>
      </Skeleton>
    </DefaultRouteTabs>
  );
};

export default BankAccountConfigBankSlipPage;
